import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Map, { Marker, NavigationControl, type MapRef } from 'react-map-gl/maplibre';
import maplibregl, { addProtocol } from 'maplibre-gl';
import type { GeoJSONSource, Map as MapLibreMap } from 'maplibre-gl';
import { Protocol } from 'pmtiles';
import {
  Alert, AlertTitle, Box, Chip, Stack, Tooltip, Typography,
  useColorScheme,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import 'maplibre-gl/dist/maplibre-gl.css';

import type { City, RoutePlan } from '../api/types';
import type { ViewBox } from '../util/viewBox';
import { formatClock } from '../util/time';
import {
  isPassedStop, passedStopCount, stopNumbering, PASSED_CLUTTER,
} from '../util/passedStops';
import { INITIAL_STYLE, applyBasemap, applyTheme } from '../map/basemapStyle';
import {
  AVOID_COLOUR, AVOID_VIOLATED_COLOUR, COVERED_COLOUR, MODE_COLOURS, RETURN_LEG_COLOUR,
  STOP_COLOURS,
  GROUP_GLYPHS,
} from '../theme';

// The pmtiles protocol handler is global to maplibre, so register it once.
let protocolRegistered = false;
function registerPmtiles() {
  if (protocolRegistered) return;
  addProtocol('pmtiles', new Protocol().tile);
  protocolRegistered = true;
}

const ROUTE_SOURCE = 'travplaces-route';
/** The moving dash that gives the route its direction of travel. */
const ROUTE_FLOW_LAYER = 'travplaces-route-flow';

/** Diagonal hatching for held ground, registered once per map. */
const HATCH_IMAGE = 'travplaces-hatch';

/**
 * A diagonal-stripe tile, drawn into a canvas at load.
 *
 * Shipped as pixels rather than as an asset: it is sixteen pixels square,
 * it has to follow the theme, and a file on disk would be one more thing to
 * serve and to cache-bust.
 */
/** "4 hr 25" — short enough for a readout, unambiguous enough to trust. */
function formatHudDuration(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const rest = Math.round(minutes % 60);
  return hours > 0 ? `${hours} hr${rest ? ` ${rest}` : ''}` : `${rest} min`;
}

function hatchImage(colour: string): ImageData | null {
  const size = 16;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.strokeStyle = colour;
  ctx.lineWidth = 2;
  ctx.lineCap = 'square';
  // Two strokes, offset by the tile, so the stripes meet across tile edges.
  for (const offset of [-size, 0]) {
    ctx.beginPath();
    ctx.moveTo(offset, size);
    ctx.lineTo(offset + size, 0);
    ctx.stroke();
  }
  return ctx.getImageData(0, 0, size, size);
}
const AVOID_SOURCE = 'travplaces-avoid';
/** City outlines, drawn on request so somebody can see what a city covers. */
const REGION_SOURCE = 'travplaces-regions';
/** The wider ground avoidance searches, shown on request beside the corridor. */
const ALT_SOURCE = 'travplaces-alternates';
/**
 * The other days of a trip, drawn behind the one being looked at.
 *
 * A separate source rather than more features in ROUTE_SOURCE, so the
 * selected day keeps every behaviour it has on the single-day screen — the
 * direction arrows, the drag-to-bend grab line, the returning-leg colour.
 * Those all read ROUTE_SOURCE and would have to learn about days otherwise.
 */
const DAYS_SOURCE = 'travplaces-trip-days';
/**
 * The ground a plan would search, shown on request.
 *
 * Drawn beneath everything else and barely tinted: it is context for the
 * route, not a thing to read. Its whole job is to answer "how far afield
 * will this look?" before anybody spends a plan finding out.
 */
const COVERAGE_SOURCE = 'travplaces-coverage';
const ROUTE_ARROW_LAYER = 'travplaces-route-arrows';


/**
 * Every layer drawn from the route source, in one place.
 *
 * Exported for the PDF export, which shows one leg at a time and so has to
 * filter all of them together. Listed rather than discovered at runtime
 * because a layer added here and forgotten there is a leg picture with half
 * the route still on it.
 */
export const ROUTE_LINE_LAYERS = [
  `${ROUTE_SOURCE}-casing`,
  `${ROUTE_SOURCE}-line`,
  ROUTE_FLOW_LAYER,
  ROUTE_ARROW_LAYER,
];

/** What each marker letter means, spelled out on hover. */
const STOP_ROLE_LABELS: Record<string, string> = {
  start: 'S — Start Stop',
  end: 'E — End Spot',
  lunch: 'Lunch',
  coffee: 'Coffee',
  suggested: 'Suggested — not in the trip yet',
};
const POI_SOURCE = 'travplaces-pois';
export const POI_LAYER = 'travplaces-poi-dots';

/*
 * The places a journey characteristic is scored on, drawn along the route.
 *
 * Separate from the category browser above, and deliberately quieter: these
 * are not places to visit, they are the evidence behind "Scenic 1.00" — the
 * parks a line was judged to pass, or the bars it was judged to avoid. Shown
 * so a slider that picks between routes can be seen doing it, which the shape
 * of a line cannot show on its own.
 */
const CHARACTER_SOURCE = 'travplaces-character-pois';
const CHARACTER_LAYER = 'travplaces-character-dots';

type FeatureCollection = {
  type: 'FeatureCollection';
  features: Record<string, unknown>[];
};

const EMPTY: FeatureCollection = { type: 'FeatureCollection', features: [] };

/**
 * Whether this browser can give MapLibre the WebGL context it needs.
 *
 * The probe hands back the context it acquires, which is the whole point of
 * writing it out rather than returning `Boolean(getContext(...))`. Browsers
 * cap how many WebGL contexts may be live at once, and this function runs on
 * every mount of the map — on three different pages. Leaking one each time
 * walked a perfectly capable browser up to the cap, after which getContext()
 * returns null and the map is a warning box for the rest of the session: the
 * map worked when the page first loaded and stopped once you had moved
 * between Plan, Saved and a trip a few times, which reads as anything but a
 * missing feature.
 *
 * Releasing it explicitly rather than leaving the canvas to be collected,
 * because collection is not guaranteed to happen before the cap is reached.
 */
function webglAvailable(): boolean {
  /*
   * A deliberate override, because a fallback nobody can reach is a fallback
   * nobody can check. `?nowebgl=1` forces the raster map on a machine whose
   * WebGL works perfectly well, which is the only practical way to look at it
   * before a browser that needs it does.
   */
  try {
    if (new URLSearchParams(window.location.search).get('nowebgl') === '1') return false;
  } catch {
    // A window without a parseable location is not worth failing over.
  }

  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl2') ?? canvas.getContext('webgl');
    if (!gl) return false;

    gl.getExtension('WEBGL_lose_context')?.loseContext();
    return true;
  } catch {
    return false;
  }
}

interface Props {
  /**
   * Handed the live map once it has loaded, and null when it goes away.
   *
   * Only the export wants this. Exporting a route to PDF means putting a
   * picture of it in the file, and a picture of a map can only come from a
   * map: there is no second renderer, and asking a tile server for one would
   * be a different map from the one on screen.
   */
  onMapReady?: (map: MapLibreMap | null) => void;
  city: City;
  plan: RoutePlan | null;
  /** Served by the API when the city's basemap has been generated. */
  basemapUrl?: string | null;
  onStopClick?: (index: number) => void;
  highlightedStopIndex?: number | null;
  /**
   * A stop to bring into view, asked for from the itinerary.
   *
   * Carries a nonce as well as the index so asking twice for the same stop
   * works: a traveller who has panned away and clicks the same row again
   * means "take me back", and an index alone would read as no change at all.
   */
  focusStop?: { index: number; nonce: number } | null;
  /**
   * Whether the places the round only passes are put down.
   *
   * Held by the page rather than here because the schedule beside the map
   * hides them in step: one switch over two panels, so what somebody reads
   * in the list is what they see on the line. Left out, the map keeps its
   * own copy.
   */
  passedHidden?: boolean;
  onPassedHiddenChange?: (hidden: boolean) => void;
  /**
   * One spot to frame the map on, instead of the whole city.
   *
   * For a map that exists to show where a single place is — the window a
   * coverage row opens, say. Without it the view opens on the city, which
   * is the right answer for a round and useless for a point: the marker is
   * a dot somewhere in a county.
   */
  focusPoint?: { lat: number; lng: number; zoom?: number };
  /** POIs for the category browser, as a GeoJSON FeatureCollection. */
  poiData?: unknown;
  /**
   * Places behind the journey characteristics that are set, already narrowed
   * to those near the drawn route. A FeatureCollection, or nothing when no
   * characteristic is asking for anything.
   */
  characterPoiData?: unknown;
  /**
   * Avoidance zones to draw before a route exists, so the traveller can see
   * how much of the city a radius actually swallows while choosing it.
   */
  previewAvoid?: { lat: number; lng: number; radiusM: number; categoryKey?: string }[];
  /** Colour per category, shared with the sidebar so the two always agree. */
  categoryColours?: Map<string, string>;
  /** Fired when the map is clicked away from any POI, to mark a bare spot. */
  onMapClick?: (point: { lat: number; lng: number }) => void;
  /**
   * Where the camera came to rest, after every move.
   *
   * For a caller that takes the map away and brings it back — a panel
   * that switches between a map and a record, say — and wants it to
   * return to the view somebody left rather than to the city it opened
   * on. Fired on rest rather than continuously: a drag is one movement,
   * not sixty.
   *
   * The bounds come with it, as [west, south, east, north], for a caller
   * that fetches what the map is showing rather than the whole city.
   */
  onCamera?: (view: {
    lat: number; lng: number; zoom: number; bounds: ViewBox;
  }) => void;
  /** Spots the traveller marked, drawn so they are visible before planning. */
  pins?: {
    lat: number;
    lng: number;
    label?: string;
    /** Street address, when the place could be identified. */
    address?: string;
    /**
     * Chosen from a list rather than dropped on the map. Draggable like any
     * other pin, and reported the same way: whether a named place that has
     * been moved is still that place is for the handler to decide.
     */
    fromList?: boolean;
    /**
     * Cannot be moved, whatever else can. A break is asked for as a place id
     * and has no coordinate form, so a drag has nowhere to put it.
     */
    fixed?: boolean;
    /**
     * `suggested` is a place being considered, not one that is in the trip.
     * Drawn hollow for exactly that reason — see the marker below.
     */
    role: 'start' | 'end' | 'visit' | 'avoid' | 'suggested';
    /** Picked out from its neighbours, for the row the cursor is on. */
    highlighted?: boolean;
    /**
     * Saved plans that already stop here, drawn on the marker itself.
     *
     * For a map being browsed rather than read: a hundred identical rings
     * answer "where are they" when the question is "which of these has
     * already been worked". Only suggestions carry it — everything else
     * on the map is in the round being built, where the question does not
     * arise. Absent means nothing is claimed either way; zero means it has
     * been looked up and the answer is none.
     */
    planned?: number;
    /**
     * The place behind the marker, where there is one. Carried so a
     * suggestion can be opened from the map — see `onPinClick`.
     */
    poiId?: string;
    /**
     * Moves this marker, when it is dragged.
     *
     * Carried on the pin for the same reason as `onRemove`: the index is a
     * position in a list the page assembles from several sources. A page
     * that owns its pins one at a time says so here; `onPinDrag` below is
     * the older index-based route, still used where the list is simple.
     */
    onMove?: (point: { lat: number; lng: number }) => void;
    /**
     * Takes this marker off the map, on a double click.
     *
     * Carried on the pin rather than fired by index, because the index is
     * a position in a list the page assembles from five sources — the two
     * ends, the named places, the avoided ones, the suggestions — and
     * mapping it back was a sum nobody should have to keep right. The page
     * already knows which list each marker came from, so it says so here.
     *
     * Absent means this one cannot be removed from the map: a suggestion
     * has not been added to anything yet, and a place that came from the
     * form belongs to the form.
     */
    onRemove?: () => void;
  }[];
  /**
   * Fired when a suggested place is clicked on the map.
   *
   * Only suggestions: every other pin is already somewhere in the round, and
   * the itinerary beside the map is where those are read about. A suggestion
   * is the one kind of marker that stands for a decision not yet made.
   */
  onPinClick?: (poiId: string) => void;
  /** A single click on a removable marker — says how to remove it. */
  onPinHint?: (role: string) => void;
  /**
   * Fired when the drawn route is dragged onto a new position, to force the
   * path through that point. Absent means the line is not draggable.
   */
  /** Fired when a marked pin is dragged, identified by its index in `pins`. */
  onPinDrag?: (index: number, point: { lat: number; lng: number }) => void;
  /**
   * Fired when the drawn route's S or E marker is dragged to a new position.
   *
   * Separate from `onPinDrag`: those markers come from the plan rather than
   * from `pins`. Absent means a planned route's ends cannot be dragged.
   */
  onEndpointDrag?: (kind: 'start' | 'end', point: { lat: number; lng: number }) => void;
  /**
   * Points the route is bent through. Drawn distinctly from stops, because
   * they are not places to visit — they only shape the line.
   */
  viaPoints?: { lat: number; lng: number }[];
  /**
   * Bends the route through a point, after the stop whose leg was grabbed.
   *
   * Fired when the handle that follows the cursor along the line is dragged
   * and let go. Absent means the line cannot be redrawn by hand.
   */
  onRouteBend?: (point: { lat: number; lng: number }, afterStopIndex: number) => void;
  /**
   * Moves a shaping point that is already placed.
   *
   * Without this the only edit available is removal: a point a few metres
   * off had to be deleted and placed again by eye. Absent means the points
   * can be removed but not adjusted.
   */
  onViaPointMove?: (index: number, point: { lat: number; lng: number }) => void;
  /** Fired when a via point is clicked, to remove it. */
  onViaPointClick?: (index: number) => void;
  /** A single click on a via point — says how to remove it, rather than doing it. */
  onViaPointHint?: (index: number) => void;
  /** Alternative routes to draw behind the chosen one, greyed out. */
  alternatives?: { index: number; geometry: [number, number][] }[];
  /** Fired when an alternative route is clicked. */
  onAlternativeClick?: (index: number) => void;
  /** Draws this alternative solid, so hovering the list points at the map. */
  highlightedAlternative?: number | null;
  /**
   * The rest of a trip's days, drawn faded behind the selected one.
   *
   * Only the geometry and a colour: these lines are context, not something to
   * interact with. The day being looked at goes through `plan` as usual, so
   * it behaves exactly as a single-day route does.
   */
  otherDays?: { index: number; colour: string; geometry: [number, number][][] }[];
  /** The search corridor as GeoJSON, or null to draw none. */
  coverage?: unknown | null;
  /**
   * Every city's outline, or null to draw none.
   *
   * A review overlay rather than part of the plan: a "city" can be a whole
   * state, so which one covers a given point is no longer self-evident, and
   * a start outside the planned city is otherwise invisible until the plan
   * comes back empty.
   */
  regions?: unknown | null;
  /** The plan is a loop, so the start marker stands for both ends. */
  roundTrip?: boolean;
  /** Fired when a POI dot is clicked, so the page can offer role actions. */
  onPoiClick?: (poi: {
    id: string; name: string; categoryKey: string; categoryLabel: string; address: string | null;
    visitable: boolean; avoidable: boolean; lng: number; lat: number;
    /* Carried so the popup can show contact details and credit the dataset. */
    source: 'osm' | 'manual' | 'fsq'; phone: string | null; website: string | null;
  }) => void;
}

/*
 * A coordinate list, whatever shape it arrived in.
 *
 * Every drawn line here is [lng, lat] pairs, and the map reads them by
 * destructuring each point — fitting the bounds, averaging an avoidance ring.
 * A point that is {lat, lng} instead raises "object is not iterable" DURING
 * RENDER, which React treats as fatal: there is no boundary above this, so
 * one malformed point blanks the whole page rather than drawing one line
 * badly.
 *
 * That is too much to lose to a stale row. Plans are cached in the assistant's
 * sessions, in saved routes and in trips, so a shape that was once written
 * wrong outlives the code that wrote it — and arrives long after anybody
 * remembers why.
 *
 * So the shape is repaired at the edge, and a point that is neither is
 * dropped rather than drawn at [0, 0] in the Atlantic.
 */
function asPairs(points: unknown): [number, number][] {
  if (!Array.isArray(points)) return [];

  return points.flatMap((point): [number, number][] => {
    if (Array.isArray(point) && point.length >= 2
      && typeof point[0] === 'number' && typeof point[1] === 'number') {
      return [[point[0], point[1]]];
    }
    if (point && typeof point === 'object') {
      const { lat, lng } = point as { lat?: unknown; lng?: unknown };
      if (typeof lat === 'number' && typeof lng === 'number') return [[lng, lat]];
    }

    return [];
  });
}

/*
 * The plan, with every coordinate list it carries put right.
 *
 * Done once at the boundary rather than at each of the four places a point is
 * destructured: those are the symptoms, and a fifth would be written one day
 * by somebody who had never heard of this.
 */
function withSaneGeometry(plan: RoutePlan | null): RoutePlan | null {
  if (!plan) return null;

  return {
    ...plan,
    legs: plan.legs.map((leg) => ({ ...leg, geometry: asPairs(leg.geometry) })),
    avoidedZones: (plan.avoidedZones ?? []).map(
      (zone) => ({ ...zone, polygon: asPairs(zone.polygon) }),
    ),
    alternatives: (plan.alternatives ?? []).map(
      (alt) => ({ ...alt, geometry: asPairs(alt.geometry) }),
    ),
  };
}

/*
  Where each city's map was last left.

  Two screens plan in the same city and neither keeps the other's map: the
  single-day form and the operation form are separate pages, and switching
  between them built a new map framed on the whole city, throwing away
  whatever ground somebody had worked their way down to. A map that opens
  where it was left is the difference between switching screen and starting
  again.

  Module-level, so every map in the tab shares it, and forgotten on a
  reload — which is exactly when framing the city is the right answer
  again.
*/
/* Keyed by city id. A record rather than a Map: `Map` is the map. */
const lastSeen: Record<string, { lng: number; lat: number; zoom: number }> = {};

export function MapView({
  city, plan: planProp, basemapUrl, onStopClick, highlightedStopIndex,
  passedHidden, onPassedHiddenChange, focusPoint,
  poiData, characterPoiData, onPoiClick, previewAvoid,
  categoryColours, onMapClick, onCamera, pins, onPinClick, roundTrip, onPinDrag, onEndpointDrag,
  focusStop,
  alternatives, onAlternativeClick, highlightedAlternative, viaPoints, onViaPointClick,
  onPinHint,
  onRouteBend, onViaPointMove,
  onViaPointHint,
  otherDays, coverage, regions, onMapReady,
}: Props) {
  // Everything below reads `plan`; nothing below needs to know it was mended.
  const plan = useMemo(() => withSaneGeometry(planProp), [planProp]);
  const mapRef = useRef<MapRef>(null);
  const theme = useTheme();
  const { mode, systemMode } = useColorScheme();
  const dark = (mode === 'system' ? systemMode : mode) === 'dark';

  const [styleReady, setStyleReady] = useState(false);

  /*
   * The handle that redraws the line, and where on the line it was grabbed.
   *
   * `leg` is the leg under the cursor when the handle appeared, which is the
   * leg the new point belongs to. Taken at hover rather than at drop because
   * by the time it is dropped the cursor is off the line, which is the whole
   * point of the gesture.
   */
  /*
   * Where the route is being dragged to, while it is being dragged.
   *
   * Null the rest of the time, which is also what says the line is not
   * currently being reshaped. The leg comes from the press, not the drop:
   * see the effect that claims the gesture.
   */
  const [bend, setBend] = useState<{ leg: number; lng: number; lat: number } | null>(null);
  /* Where a shaping point has been dragged to, while it is being dragged. */
  const [viaDrag, setViaDrag] = useState<
    { index: number; lng: number; lat: number } | null
  >(null);

  /*
    Bring one stop into view, without changing how the route is framed.

    `easeTo` rather than `fitBounds`: the route's extent is what the other
    effects here are for, and recomputing it around a single stop would
    throw away the view the traveller is reading. The zoom is only ever
    raised — somebody looking at the whole round who clicks a row wants to
    see that stop, while somebody already close in does not want to be
    pulled back out.
  */
  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map || !styleReady || !focusStop) return;

    const stop = plan?.stops[focusStop.index];
    if (!stop) return;

    map.easeTo({
      center: [stop.lng, stop.lat],
      zoom: Math.max(map.getZoom(), 14),
      duration: 600,
    });
    // The nonce is what makes a repeat click move the map again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusStop?.nonce, styleReady]);
  const [mapError, setMapError] = useState<string | null>(null);
  const [supported] = useState(webglAvailable);

  registerPmtiles();

  // One feature per leg, so each is coloured by its own travel mode — a
  // mixed-mode itinerary shows walking and taxi segments distinctly.
  /* Same place to within a few metres — a loop rather than a journey. */
  const isRoundTrip = useMemo(() => {
    const stops = plan?.stops ?? [];
    if (stops.length < 2) return false;
    const first = stops[0]!;
    const last = stops[stops.length - 1]!;
    return Math.abs(first.lat - last.lat) < 1e-4 && Math.abs(first.lng - last.lng) < 1e-4;
  }, [plan]);

  /*
   * Whether the round stops nowhere at all.
   *
   * A mixed round has numbered discs to count along, so the diamonds between
   * them can stay small and blank — they are the quiet parts of a sequence
   * somebody can already read. A round made ENTIRELY of them has nothing
   * else to count by, so each carries its number.
   */
  /*
   * The passed places start put down.
   *
   * Two hundred diamonds over a city centre is the route somewhere
   * underneath them: at that size they stop describing the line and start
   * hiding it. A round carrying that many opens with them away and the line
   * plain, and nothing has left the plan — one click brings them back,
   * and where there are few enough to read they were never hidden at all.
   *
   * The page may hold the switch instead, so the schedule hides them in
   * step; without one the map keeps its own.
   */
  const [viaHiddenOwn, setViaHiddenOwn] = useState(true);
  const viaHidden = passedHidden ?? viaHiddenOwn;
  const setViaHidden = (next: boolean) => {
    setViaHiddenOwn(next);
    onPassedHiddenChange?.(next);
  };

  const passingCount = useMemo(() => passedStopCount(plan), [plan]);
  const passingCrowd = passingCount >= PASSED_CLUTTER;
  const passedDown = viaHidden && passingCrowd;

  /* What the discs say. Counts only what is drawn: see stopNumbering. */
  const stopNumbers = useMemo(() => stopNumbering(plan, passedDown), [plan, passedDown]);

  const everyStopPassing = useMemo(() => {
    const between = (plan?.stops ?? []).filter(
      (s) => s.kind !== 'start' && s.kind !== 'end',
    );

    return between.length > 0 && between.every((s) => s.dwellMinutes === 0);
  }, [plan]);

  /*
   * The stop furthest from the start, which is where a loop turns for home.
   *
   * Splitting the legs at the halfway index would put the turn in an
   * arbitrary place when the stops are unevenly spread. The furthest point is
   * what anyone would actually call the far end of the trip.
   */
  const turningLegIndex = useMemo(() => {
    if (!isRoundTrip || !plan || plan.stops.length < 3) return -1;
    const start = plan.stops[0]!;
    let furthest = 1;
    let best = -1;
    plan.stops.slice(1, -1).forEach((stop, i) => {
      const d = (stop.lat - start.lat) ** 2 + (stop.lng - start.lng) ** 2;
      if (d > best) { best = d; furthest = i + 1; }
    });
    return furthest;
  }, [isRoundTrip, plan]);

  const routeData = useMemo<FeatureCollection>(() => {
    if (!plan) return EMPTY;
    return {
      type: 'FeatureCollection',
      features: plan.legs
        .filter((leg) => leg.geometry.length >= 2)
        .map((leg, index) => {
          // Legs after the turning point are the way back.
          const returning = turningLegIndex >= 0 && leg.fromIndex >= turningLegIndex;
          return {
            type: 'Feature' as const,
            id: index,
            properties: {
              colour: returning ? RETURN_LEG_COLOUR : MODE_COLOURS[leg.mode],
              legIndex: index,
              returning,
            },
            geometry: { type: 'LineString' as const, coordinates: leg.geometry },
          };
        }),
    };
  }, [plan, turningLegIndex]);

  const regionData = useMemo<FeatureCollection>(() => {
    if (!regions) return EMPTY;
    return regions as FeatureCollection;
  }, [regions]);

  const coverageData = useMemo<FeatureCollection>(() => {
    if (!coverage) return EMPTY;
    return {
      type: 'FeatureCollection',
      features: [{ type: 'Feature', properties: {}, geometry: coverage as never }],
    };
  }, [coverage]);

  const daysData = useMemo<FeatureCollection>(() => {
    if (!otherDays?.length) return EMPTY;
    return {
      type: 'FeatureCollection',
      features: otherDays.flatMap((day) =>
        day.geometry
          .filter((line) => line.length >= 2)
          .map((line, i) => ({
            type: 'Feature' as const,
            id: day.index * 1000 + i,
            properties: { colour: day.colour, day: day.index },
            geometry: { type: 'LineString' as const, coordinates: line },
          }))),
    };
  }, [otherDays]);

  /*
   * The one alternative being pointed at, or nothing.
   *
   * Carried in the DATA rather than selected out of it with a layer filter.
   * Every other way round used to be in this source at once, with the hovered
   * one picked by `setFilter` — and after switching routes the first hover
   * drew nothing while the second drew correctly, because the filter and the
   * source were being changed against each other in the same frame.
   *
   * `setData` is how the route itself is drawn and has never missed, so the
   * hovered line is now drawn the same way: one feature in, one line out,
   * nothing to re-evaluate against data that may or may not have settled.
   */
  const altData = useMemo<FeatureCollection>(() => {
    const at = highlightedAlternative ?? -1;
    const shown = alternatives?.find((alt) => alt.index === at);
    if (!shown || shown.geometry.length < 2) return EMPTY;

    return {
      type: 'FeatureCollection',
      features: [{
        type: 'Feature',
        id: shown.index,
        properties: { index: shown.index },
        geometry: { type: 'LineString', coordinates: shown.geometry },
      }],
    };
  }, [alternatives, highlightedAlternative]);

  /*
   * Before a route is planned there are no server-built buffers, so draw the
   * circles here. Twenty-four segments is visually round at city zoom and
   * matches what the server generates, so the preview does not jump when the
   * real plan arrives.
   */
  const previewFeatures = useMemo<FeatureCollection['features']>(() => {
    if (!previewAvoid?.length) return [];
    return previewAvoid.map((zone) => {
      const ring: [number, number][] = [];
      const latRad = (zone.lat * Math.PI) / 180;
      for (let i = 0; i <= 24; i++) {
        const angle = (2 * Math.PI * i) / 24;
        ring.push([
          zone.lng + (zone.radiusM / (111_320 * Math.cos(latRad))) * Math.cos(angle),
          zone.lat + (zone.radiusM / 110_540) * Math.sin(angle),
        ]);
      }
      return {
        type: 'Feature',
        properties: {
          colour: (zone.categoryKey && categoryColours?.get(zone.categoryKey)) || AVOID_COLOUR,
        },
        geometry: { type: 'Polygon', coordinates: [ring] },
      };
    });
  }, [previewAvoid, categoryColours]);

  const avoidData = useMemo<FeatureCollection>(() => {
    /*
     * The plan's own buffers, plus anything chosen since it was planned.
     *
     * A planned route carries authoritative zones — they are what the router
     * was actually given, and they know which were crossed. But taking ONLY
     * those meant that adding a category after planning drew nothing at all
     * until a re-plan came back, so the map denied a change the form had
     * plainly accepted. The preview fills that gap and is dropped again as
     * soon as the plan catches up, since a zone the plan already holds is
     * not added twice.
     */
    const planned = (plan?.avoidedZones ?? [])
      .filter((zone) => zone.polygon.length >= 4)
      .map((zone) => ({
        type: 'Feature' as const,
        properties: {
          colour: zone.violated
            ? AVOID_VIOLATED_COLOUR
            : categoryColours?.get(zone.categoryKey) ?? AVOID_COLOUR,
        },
        geometry: { type: 'Polygon' as const, coordinates: [zone.polygon] },
      }));

    // Rounded to about ten metres: the same place buffered twice lands on
    // the same key, and two genuinely different places never do.
    const key = (feature: { geometry?: unknown }) => {
      const ring = (feature.geometry as { coordinates?: [number, number][][] } | undefined)
        ?.coordinates?.[0] ?? [];
      if (ring.length === 0) return '';
      const sum = ring.reduce((a, [lng, lat]) => [a[0] + lng, a[1] + lat], [0, 0]);
      return [sum[0] / ring.length, sum[1] / ring.length]
        .map((n) => n.toFixed(4)).join(',');
    };

    const held = new Set(planned.map(key));
    const polygons = plan
      ? [...planned, ...previewFeatures.filter((f) => !held.has(key(f)))]
      : previewFeatures;

    /*
     * A point at the middle of every buffer, so the dot layer has something
     * to draw. A buffer is true ground distance and 500 m of it is under a
     * pixel once a state is on screen — which made avoiding fifty-seven
     * police stations look like a setting that had not taken.
     *
     * Averaging the ring is exact enough for a marker: these are circles.
     */
    const centres = polygons.map((f) => {
      const ring = (f.geometry as { coordinates: [number, number][][] }).coordinates[0] ?? [];
      const sum = ring.reduce((a, [lng, lat]) => [a[0] + lng, a[1] + lat], [0, 0]);
      return {
        type: 'Feature' as const,
        properties: { ...(f.properties ?? {}), centre: true },
        geometry: {
          type: 'Point' as const,
          coordinates: ring.length
            ? [sum[0] / ring.length, sum[1] / ring.length]
            : [0, 0],
        },
      };
    });

    return { type: 'FeatureCollection', features: [...polygons, ...centres] } as FeatureCollection;
  }, [plan, previewFeatures, categoryColours]);

  /**
   * Builds a MapLibre `match` expression mapping categoryKey -> colour.
   *
   * Done as an expression rather than baking colours into each feature, so a
   * palette change repaints without refetching any data.
   */
  const colourExpression = useMemo(() => {
    const pairs = [...(categoryColours?.entries() ?? [])].flatMap(([key, colour]) => [key, colour]);
    if (pairs.length === 0) return AVOID_COLOUR;
    return ['match', ['get', 'categoryKey'], ...pairs, AVOID_COLOUR] as unknown as string;
  }, [categoryColours]);

  /**
   * Route layers are added through MapLibre's own API rather than
   * react-map-gl's <Source>/<Layer> components.
   *
   * Those components decide whether to add a layer by reading MapLibre's
   * private `style._loaded` field. That coupling breaks silently across
   * MapLibre versions — the map renders, DOM markers appear, and the layers
   * simply never arrive with nothing logged. Driving the documented API and
   * gating on the public `styledata` event removes that failure mode.
   */
  /*
   * The latest `syncLayers`, reachable from a callback queued by an older one.
   *
   * A retry scheduled while the map was busy must apply what is true WHEN IT
   * RUNS, not what was true when it was queued — otherwise pointing at one
   * row and then another would redraw the first.
   */
  const syncRef = useRef<((map: MapLibreMap) => void) | null>(null);
  const retryQueued = useRef(false);

  const syncLayers = useCallback(
    (map: MapLibreMap) => {
      /*
       * Work arriving while the map is busy is deferred, not dropped.
       *
       * This used to `return`, on the reasoning that a style still loading
       * has no layers to update and `styledata` would bring us back. It does
       * — once. Switching to another way round re-sends the route's own
       * source, which leaves the style busy for a moment; a hover landing
       * inside that moment was discarded, and `styledata` had already fired,
       * so nothing ever re-applied it. The line appeared on the SECOND hover,
       * by which time the map had settled. Every layer went through here, so
       * every one of them could be silently skipped this way.
       *
       * `idle` rather than `styledata`: it fires once the map has finished
       * loading and drawing, which is the condition actually being waited
       * for. One retry is queued at a time; the ref makes it the current
       * state that gets drawn rather than the stale one that queued it.
       */
      if (!map.isStyleLoaded()) {
        if (!retryQueued.current) {
          retryQueued.current = true;
          // `void`: the listener overload returns the map, but the typings
          // also carry a promise-returning one and the linter cannot tell
          // which is in play.
          void map.once('idle', () => {
            retryQueued.current = false;
            syncRef.current?.(map);
          });
        }

        return;
      }

      applyTheme(map, dark);

      if (basemapUrl) applyBasemap(map, basemapUrl, dark, `${AVOID_SOURCE}-fill`);

      const casing = dark ? '#0b100f' : '#ffffff';

      // Avoidance zones go in first so the route draws on top of them.
      if (!map.getSource(COVERAGE_SOURCE)) {
        map.addSource(COVERAGE_SOURCE, { type: 'geojson', data: coverageData as never });
        map.addLayer({
          id: `${COVERAGE_SOURCE}-fill`,
          type: 'fill',
          source: COVERAGE_SOURCE,
          paint: { 'fill-color': '#1d6f6a', 'fill-opacity': 0.14 },
        });
        map.addLayer({
          /*
           * Wide enough to read at the zoom a cross-country journey is looked
           * at from. The band follows the roads now rather than the straight
           * line between the ends, so at three kilometres either side it is
           * about two pixels across a state — a hairline under the route line
           * drawn on top of it, which is indistinguishable from the toggle
           * doing nothing.
           */
          id: `${COVERAGE_SOURCE}-edge`,
          type: 'line',
          source: COVERAGE_SOURCE,
          paint: {
            'line-color': '#1d6f6a',
            'line-width': 2.5,
            'line-opacity': 0.9,
            'line-dasharray': [3, 3],
          },
        });
      } else {
        (map.getSource(COVERAGE_SOURCE) as GeoJSONSource).setData(coverageData as never);
      }

      if (!map.getSource(REGION_SOURCE)) {
        map.addSource(REGION_SOURCE, { type: 'geojson', data: regionData as never });
        map.addLayer({
          id: `${REGION_SOURCE}-fill`,
          type: 'fill',
          source: REGION_SOURCE,
          paint: { 'fill-color': '#245e94', 'fill-opacity': 0.1 },
        });
        map.addLayer({
          id: `${REGION_SOURCE}-line`,
          type: 'line',
          source: REGION_SOURCE,
          paint: {
            'line-color': '#245e94',
            'line-width': 1.5,
            // A stored boundary is the real outline; a bounding box is a
            // rectangle nothing actually is, and says so by being dashed.
            'line-dasharray': ['case', ['get', 'exact'], ['literal', [1, 0]], ['literal', [4, 3]]],
            'line-opacity': 0.75,
          },
        });
        map.addLayer({
          id: `${REGION_SOURCE}-label`,
          type: 'symbol',
          source: REGION_SOURCE,
          layout: {
            'text-field': ['get', 'name'],
            'text-font': ['Noto Sans Medium'],
            'text-size': 13,
            'symbol-placement': 'line',
          },
          paint: {
            'text-color': '#245e94',
            'text-halo-color': dark ? '#0b100f' : '#ffffff',
            'text-halo-width': 1.6,
          },
        });
      } else {
        (map.getSource(REGION_SOURCE) as GeoJSONSource).setData(regionData as never);
      }

      if (!map.getSource(AVOID_SOURCE)) {
        map.addSource(AVOID_SOURCE, { type: 'geojson', data: avoidData as never });
        map.addLayer({
          id: `${AVOID_SOURCE}-fill`,
          type: 'fill',
          source: AVOID_SOURCE,
          filter: ['!', ['has', 'centre']],
          paint: { 'fill-color': ['get', 'colour'], 'fill-opacity': 0.13 },
        });
        /*
         * Hatched, the way ground that is held is drawn on paper.
         *
         * A flat wash reads as "something is shaded here"; diagonal hatching
         * reads as an instruction, and it survives overlapping — fifty
         * translucent circles over a city become one indistinct smear, while
         * the stripes stay legible because they do not accumulate.
         *
         * The pattern is drawn into a canvas at load rather than shipped as
         * an asset, so it costs nothing to serve and can follow the theme.
         */
        if (map.getImage(HATCH_IMAGE)) {
          map.addLayer({
            id: `${AVOID_SOURCE}-hatch`,
            type: 'fill',
            source: AVOID_SOURCE,
            filter: ['!', ['has', 'centre']],
            paint: { 'fill-pattern': HATCH_IMAGE, 'fill-opacity': 0.5 },
          });
        }
        /*
         * A dot per avoided place, sized in pixels rather than metres.
         *
         * The buffers below are true ground distance, which is right — but a
         * 500 m circle is under a pixel across once a whole state is on
         * screen, so avoiding fifty-seven police stations drew nothing at all
         * and read as a setting that had not taken. This keeps them visible
         * at any zoom, and fades out as the real buffer grows big enough to
         * speak for itself.
         */
        map.addLayer({
          id: `${AVOID_SOURCE}-dot`,
          type: 'circle',
          source: AVOID_SOURCE,
          filter: ['has', 'centre'],
          paint: {
            'circle-color': ['get', 'colour'],
            'circle-radius': ['interpolate', ['linear'], ['zoom'], 4, 2.5, 10, 4, 13, 5],
            'circle-opacity': ['interpolate', ['linear'], ['zoom'], 11, 0.85, 14, 0],
            'circle-stroke-width': 1,
            'circle-stroke-color': casing,
            'circle-stroke-opacity': ['interpolate', ['linear'], ['zoom'], 11, 0.9, 14, 0],
          },
        });
        map.addLayer({
          id: `${AVOID_SOURCE}-outline`,
          type: 'line',
          source: AVOID_SOURCE,
          filter: ['!', ['has', 'centre']],
          paint: {
            'line-color': ['get', 'colour'],
            'line-width': 1.5,
            'line-dasharray': [3, 2],
          },
        });
      } else {
        (map.getSource(AVOID_SOURCE) as GeoJSONSource).setData(avoidData as never);
      }

      // Characteristic evidence: under everything, and quiet enough to read
      // the map through. Hollow rings rather than filled dots, so they cannot
      // be mistaken for the category browser's places or for stops.
      if (!map.getSource(CHARACTER_SOURCE)) {
        map.addSource(CHARACTER_SOURCE, {
          type: 'geojson', data: (characterPoiData ?? EMPTY) as never,
        });
        map.addLayer({
          id: CHARACTER_LAYER,
          type: 'circle',
          source: CHARACTER_SOURCE,
          paint: {
            'circle-color': 'transparent',
            'circle-radius': ['interpolate', ['linear'], ['zoom'], 10, 4, 14, 8, 18, 13],
            'circle-stroke-width': 1.6,
            /*
             * Sought and shunned cannot look alike.
             *
             * Bars drawn the same as parks read as somewhere the route is
             * taking you, when Quiet chose this line precisely because it
             * gets furthest from them. Teal for what a characteristic wants
             * near, amber for what it wants away.
             */
            'circle-stroke-color': [
              'match', ['get', 'characterSense'],
              'avoid', dark ? '#e0a458' : '#a1651a',
              dark ? '#7fd1c1' : '#1f7a68',
            ] as never,
            'circle-stroke-opacity': 0.75,
          },
        });
      } else {
        (map.getSource(CHARACTER_SOURCE) as GeoJSONSource)
          .setData(characterPoiData ?? EMPTY);
      }

      // Category-browser POIs: below the route so the itinerary stays legible.
      if (!map.getSource(POI_SOURCE)) {
        map.addSource(POI_SOURCE, { type: 'geojson', data: (poiData ?? EMPTY) as never });
        map.addLayer({
          id: POI_LAYER,
          type: 'circle',
          source: POI_SOURCE,
          paint: {
            'circle-color': colourExpression as never,
            'circle-radius': ['interpolate', ['linear'], ['zoom'], 10, 3, 14, 7, 18, 12],
            'circle-stroke-width': 1.4,
            'circle-stroke-color': dark ? '#0d1214' : '#ffffff',
            'circle-opacity': 0.92,
          },
        });
        // A glyph per group on top of the dot: safety reads as +, food as a
        // filled circle, and so on, so kinds are separable without the legend.
        map.addLayer({
          id: `${POI_LAYER}-glyph`,
          type: 'symbol',
          source: POI_SOURCE,
          minzoom: 12,
          layout: {
            'text-field': [
              'match', ['get', 'group'],
              ...Object.entries(GROUP_GLYPHS).flatMap(([g, ch]) => [g, ch]),
              GROUP_GLYPHS.other,
            ],
            'text-font': ['Noto Sans Medium'],
            'text-size': ['interpolate', ['linear'], ['zoom'], 12, 8, 18, 13],
            'text-allow-overlap': true,
            'text-ignore-placement': true,
          },
          paint: { 'text-color': '#ffffff' },
        } as never);
      } else {
        (map.getSource(POI_SOURCE) as GeoJSONSource).setData((poiData ?? EMPTY) as never);
        map.setPaintProperty(POI_LAYER, 'circle-stroke-color', dark ? '#0d1214' : '#ffffff');
        map.setPaintProperty(POI_LAYER, 'circle-color', colourExpression as never);
      }

      /*
       * Alternatives are added before the route so the chosen one always
       * draws on top of them — a dashed grey line under the live route reads
       * as "the other way round", where over it would read as the answer.
       */
      if (!map.getSource(ALT_SOURCE)) {
        map.addSource(ALT_SOURCE, { type: 'geojson', data: altData as never });
        map.addLayer({
          id: `${ALT_SOURCE}-line`,
          type: 'line',
          source: ALT_SOURCE,
          layout: { 'line-cap': 'round', 'line-join': 'round' },
          paint: {
            'line-color': dark ? '#8aa0a8' : '#6b7b82',
            'line-width': 6,
            'line-opacity': 1,
            'line-dasharray': [2, 1.5],
          },
        });
      } else {
        (map.getSource(ALT_SOURCE) as GeoJSONSource).setData(altData as never);
        map.setPaintProperty(`${ALT_SOURCE}-line`, 'line-color', dark ? '#8aa0a8' : '#6b7b82');
      }

      /*
       * Added before ROUTE_SOURCE so the day being looked at always draws on
       * top of the others. Thin, dashed and half-transparent: enough to see
       * where the rest of the trip goes, not enough to compete with it.
       */
      if (!map.getSource(DAYS_SOURCE)) {
        map.addSource(DAYS_SOURCE, { type: 'geojson', data: daysData as never });
        map.addLayer({
          id: `${DAYS_SOURCE}-line`,
          type: 'line',
          source: DAYS_SOURCE,
          layout: { 'line-cap': 'round', 'line-join': 'round' },
          paint: {
            'line-color': ['get', 'colour'],
            'line-width': 3,
            'line-opacity': 0.45,
            'line-dasharray': [1.5, 1.5],
          },
        });
      } else {
        (map.getSource(DAYS_SOURCE) as GeoJSONSource).setData(daysData as never);
      }

      if (!map.getSource(ROUTE_SOURCE)) {
        map.addSource(ROUTE_SOURCE, { type: 'geojson', data: routeData as never });
        map.addLayer({
          id: `${ROUTE_SOURCE}-casing`,
          type: 'line',
          source: ROUTE_SOURCE,
          layout: { 'line-cap': 'round', 'line-join': 'round' },
          paint: { 'line-color': casing, 'line-width': 8, 'line-opacity': 0.85 },
        });
        map.addLayer({
          id: `${ROUTE_SOURCE}-line`,
          type: 'line',
          source: ROUTE_SOURCE,
          layout: { 'line-cap': 'round', 'line-join': 'round' },
          paint: { 'line-color': ['get', 'colour'], 'line-width': 4.5 },
        });
        /*
         * Arrows along the line, showing which way round it goes.
         *
         * A plain chevron from the basemap's own font rather than a sprite
         * image: it needs no extra asset, and the glyph server already serves
         * the Latin range. `text-keep-upright: false` lets them point
         * backwards where the route doubles back, which is the whole point.
         */
        /*
         * The direction of travel, moving.
         *
         * A dashed line laid over the solid one and slid along it a few
         * times a second: the route stops being a shape on a map and starts
         * being a movement with a direction, which is the thing an operator
         * is actually looking at. The dash offset is animated rather than
         * the geometry, so it costs one paint property per frame and nothing
         * is recomputed.
         */
        map.addLayer({
          id: ROUTE_FLOW_LAYER,
          type: 'line',
          source: ROUTE_SOURCE,
          layout: { 'line-cap': 'butt', 'line-join': 'round' },
          paint: {
            'line-color': '#ffffff',
            'line-width': 2,
            'line-opacity': 0.55,
            'line-dasharray': [0, 4, 3],
          },
        });

        map.addLayer({
          id: ROUTE_ARROW_LAYER,
          type: 'symbol',
          source: ROUTE_SOURCE,
          layout: {
            'symbol-placement': 'line',
            'symbol-spacing': 90,
            'text-field': '>',
            'text-font': ['Noto Sans Medium'],
            'text-size': 13,
            'text-rotation-alignment': 'map',
            'text-keep-upright': false,
            'text-allow-overlap': true,
            'text-ignore-placement': true,
            'text-padding': 0,
          },
          paint: {
            'text-color': '#ffffff',
            'text-halo-color': ['get', 'colour'],
            'text-halo-width': 1.6,
          },
        } as never);

      } else {
        (map.getSource(ROUTE_SOURCE) as GeoJSONSource).setData(routeData as never);
        map.setPaintProperty(`${ROUTE_SOURCE}-casing`, 'line-color', casing);
      }

      /*
       * Re-assert the stack, bottom to top.
       *
       * A layer is added where the style happens to be when its source first
       * appears, and `applyBasemap` inserts its own layers before the avoid
       * fill — so after a theme swap the basemap is rebuilt ON TOP of the
       * coverage and region overlays, and a toggle that is doing exactly what
       * it is told draws nothing anybody can see. Moving each in turn costs
       * one array splice per layer and removes the whole class of problem.
       */
      for (const id of [
        `${COVERAGE_SOURCE}-fill`, `${COVERAGE_SOURCE}-edge`,
        `${REGION_SOURCE}-fill`, `${REGION_SOURCE}-line`, `${REGION_SOURCE}-label`,
        `${AVOID_SOURCE}-fill`, `${AVOID_SOURCE}-outline`, `${AVOID_SOURCE}-dot`,
        `${DAYS_SOURCE}-line`, `${ALT_SOURCE}-line`,
        `${ROUTE_SOURCE}-casing`, `${ROUTE_SOURCE}-line`,
        ROUTE_ARROW_LAYER,
        POI_LAYER, `${POI_LAYER}-glyph`,
      ]) {
        if (map.getLayer(id)) map.moveLayer(id);
      }
    },
    // Not `highlightedAlternative`: it decides what `altData` CONTAINS now,
    // rather than which of its features to show, so the data covers it.
    [routeData, altData, daysData, coverageData, regionData, avoidData, dark, basemapUrl,
     poiData, characterPoiData, colourExpression],
  );

  // Kept current for the retry above, which may run long after the render
  // that queued it.
  useEffect(() => { syncRef.current = syncLayers; }, [syncLayers]);

  // Re-apply whenever the data changes, and again after a style swap — a new
  // style discards every source and layer that was added to the old one.
  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map || !styleReady) return;

    syncLayers(map);
    const onStyleData = () => syncLayers(map);
    map.on('styledata', onStyleData);
    return () => {
      map.off('styledata', onStyleData);
    };
  }, [syncLayers, styleReady]);

  // Clicking a POI dot hands it back to the page, which offers the role
  // choices (visit / avoid / start / end).
  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map || !styleReady || !onPoiClick) return;

    const onClick = (event: maplibregl.MapLayerMouseEvent) => {
      const feature = event.features?.[0];
      if (!feature) return;
      const p = feature.properties ?? {};
      const [lng, lat] = (feature.geometry as unknown as { coordinates: [number, number] }).coordinates;
      onPoiClick({
        id: String(p.id),
        name: String(p.name),
        // The key, not just the label: callers decide what a place can be
        // used for by matching it, and labels are for reading.
        categoryKey: String(p.categoryKey ?? ''),
        categoryLabel: String(p.categoryLabel ?? ''),
        address: p.address ? String(p.address) : null,
        visitable: p.visitable === true || p.visitable === 'true',
        avoidable: p.avoidable === true || p.avoidable === 'true',
        lng, lat,
        /*
         * MapLibre stringifies feature properties, so an absent value can
         * arrive as the literal "null" rather than null. Coerced here so the
         * popup can test truthiness the obvious way.
         */
        source: (p.source as 'osm' | 'manual' | 'fsq') || 'osm',
        phone: p.phone && p.phone !== 'null' ? String(p.phone) : null,
        website: p.website && p.website !== 'null' ? String(p.website) : null,
      });
    };
    const enter = () => { map.getCanvas().style.cursor = 'pointer'; };
    const leave = () => { map.getCanvas().style.cursor = ''; };

    /*
     * Both layers, one handler.
     *
     * The characteristic rings are the same places from the same endpoint,
     * carrying the same properties — so a ring opens the dialog a dot opens,
     * and "what is this park the route was scored on" is answerable by
     * clicking it. A separate handler would have been a second way for the
     * same question to be answered differently.
     */
    const layers = [POI_LAYER, CHARACTER_LAYER];
    for (const layer of layers) {
      map.on('click', layer, onClick);
      map.on('mouseenter', layer, enter);
      map.on('mouseleave', layer, leave);
    }

    return () => {
      for (const layer of layers) {
        map.off('click', layer, onClick);
        map.off('mouseenter', layer, enter);
        map.off('mouseleave', layer, leave);
      }
    };
  }, [styleReady, onPoiClick]);

  /*
   * Right-click marks a spot.
   *
   * On the secondary button rather than the primary one so that panning and
   * POI selection — the things done constantly — never drop an unwanted pin.
   * No hit test is needed: left-click stays with the POI dialog, so the two
   * no longer compete and right-click works over a POI as well as empty map.
   */
  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map || !styleReady || !onMapClick) return;

    const onMarkSpot = (event: maplibregl.MapMouseEvent) => {
      // Suppress the browser's own menu, which would cover the dialog.
      event.preventDefault();
      event.originalEvent.preventDefault();
      onMapClick({ lat: event.lngLat.lat, lng: event.lngLat.lng });
    };

    map.on('contextmenu', onMarkSpot);
    return () => { map.off('contextmenu', onMarkSpot); };
  }, [styleReady, onMapClick]);

  /*
   * The line itself is the handle.
   *
   * Three shapes were tried before this one. A dot that followed the cursor
   * was always a frame behind it, so the press landed on the map and
   * panned. A dot per leg could only take hold of a leg in one place. Dots
   * spaced along the line worked but were clutter on a round that is
   * already carrying numbered stops, exclusion zones and direction arrows.
   *
   * So: press anywhere on the route and drag. The press is claimed on the
   * line's own layer with preventDefault, which is how MapLibre is meant to
   * be told that this gesture is not a pan; everywhere else on the map
   * still pans as it always did. The cost is that a pan which happens to
   * START on the line is taken as a bend instead, and that is the trade
   * every map people already use makes.
   *
   * A press that does not travel is not a drag. Below the threshold nothing
   * is sent, so clicking the line to read it still works.
   */
  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map || !styleReady || !onRouteBend) return;

    const layers = ROUTE_LINE_LAYERS.filter((id) => map.getLayer(id));
    if (layers.length === 0) return;

    /** How far the pointer must travel before this counts as a drag. */
    const MOVED = 6;

    /**
     * How close to a marker the line stops answering, in pixels.
     *
     * Every stop, every marked spot and every shaping point sits ON the
     * route, so the line's own grab area runs underneath all of them. A
     * press a few pixels off an S or E was landing on the line and bending
     * the path instead of moving the endpoint — the two gestures look
     * identical and only one of them is ever meant.
     *
     * Wider than the markers themselves: a stop circle is 26px across, and
     * the point of this is to cover the misses, not the hits.
     */
    const MARKER_GUARD = 22;

    let leg = 0;
    let from: { x: number; y: number } | null = null;
    let dragging = false;

    const down = (event: maplibregl.MapLayerMouseEvent) => {
      /*
        Anything with a marker on it keeps its own gesture.

        Left to the line, a near-miss on a stop became a bend, and the stop
        a traveller meant to drag stayed where it was.
      */
      const near = (lng: number, lat: number) => {
        const at = map.project([lng, lat]);
        const dx = at.x - event.point.x;
        const dy = at.y - event.point.y;

        return dx * dx + dy * dy < MARKER_GUARD * MARKER_GUARD;
      };
      const guarded = [
        ...(plan?.stops ?? []),
        ...(pins ?? []),
        ...(viaPoints ?? []),
      ];
      if (guarded.some((at) => near(at.lng, at.lat))) return;

      const legIndex: unknown = event.features?.[0]?.properties?.['legIndex'];
      leg = typeof legIndex === 'number' ? legIndex : 0;
      from = { x: event.point.x, y: event.point.y };
      dragging = false;
      // Claims the gesture: without this the map pans under the cursor.
      event.preventDefault();
    };

    const move = (event: maplibregl.MapMouseEvent) => {
      if (!from) return;
      if (!dragging) {
        const dx = event.point.x - from.x;
        const dy = event.point.y - from.y;
        if (dx * dx + dy * dy < MOVED * MOVED) return;
        dragging = true;
        map.getCanvas().style.cursor = 'grabbing';
      }
      setBend({ leg, lng: event.lngLat.lng, lat: event.lngLat.lat });
    };

    const up = (event: maplibregl.MapMouseEvent) => {
      if (!from) return;
      const travelled = dragging;
      from = null;
      dragging = false;
      map.getCanvas().style.cursor = '';
      setBend(null);
      if (travelled) {
        onRouteBend({ lat: event.lngLat.lat, lng: event.lngLat.lng }, leg);
      }
    };

    const enter = () => { if (!from) map.getCanvas().style.cursor = 'grab'; };
    // A marker's own cursor wins back over the line's: see MARKER_GUARD.
    const leave = () => { if (!from) map.getCanvas().style.cursor = ''; };

    for (const id of layers) {
      map.on('mousedown', id, down);
      map.on('mouseenter', id, enter);
      map.on('mouseleave', id, leave);
    }
    map.on('mousemove', move);
    map.on('mouseup', up);

    return () => {
      for (const id of layers) {
        map.off('mousedown', id, down);
        map.off('mouseenter', id, enter);
        map.off('mouseleave', id, leave);
      }
      map.off('mousemove', move);
      map.off('mouseup', up);
      map.getCanvas().style.cursor = '';
    };
  }, [styleReady, onRouteBend, plan, pins, viaPoints]);
  /* Clicking a dashed alternative switches the route to it. */
  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map || !styleReady || !onAlternativeClick) return;

    const LAYER = `${ALT_SOURCE}-line`;
    if (!map.getLayer(LAYER)) return;

    const click = (event: maplibregl.MapLayerMouseEvent) => {
      const index = event.features?.[0]?.properties?.index;
      if (typeof index === 'number') onAlternativeClick(index);
    };
    const enter = () => { map.getCanvas().style.cursor = 'pointer'; };
    const leave = () => { map.getCanvas().style.cursor = ''; };

    map.on('click', LAYER, click);
    map.on('mouseenter', LAYER, enter);
    map.on('mouseleave', LAYER, leave);
    return () => {
      map.off('click', LAYER, click);
      map.off('mouseenter', LAYER, enter);
      map.off('mouseleave', LAYER, leave);
    };
  }, [styleReady, onAlternativeClick, altData]);

  /*
   * Keep the canvas the size of its container.
   *
   * MapLibre measures once and then listens for window resizes. It never
   * hears about a container that changed size while hidden, so a map revealed
   * by a layout switch keeps whatever size it was built at — 400x300 if it
   * was never visible at all. A ResizeObserver covers both cases, and window
   * resizing keeps working as before.
   */
  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map) return;
    const container = map.getContainer();

    const observer = new ResizeObserver(() => {
      // Zero while hidden; resizing to that would only have to be undone.
      if (container.clientWidth > 0 && container.clientHeight > 0) map.resize();
    });
    observer.observe(container);
    return () => observer.disconnect();
    // Waits for the style, because that is the first moment the map instance
    // is certain to exist — react-map-gl creates it asynchronously, so on
    // mount the ref is still null and there is nothing to observe.
  }, [styleReady]);

  /*
   * Frame the city whenever a different one is chosen.
   *
   * `initialViewState` is exactly that — react-map-gl reads it once at mount
   * and ignores it afterwards, so switching city reloaded its places and its
   * basemap while leaving the camera over the old one. The dots for the new
   * city were on screen the whole time, just several hundred miles away.
   *
   * Safe to run unconditionally because the page clears the plan when the city
   * changes, so this can never fight the itinerary framing below.
   */
  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map) return;

    /*
      A map asked for one spot is not asked for the city.

      This effect frames the whole of it on load, which is what a planner
      wants and the opposite of what a window about a single place wants —
      and it runs after the caller's own jumpTo, so without this it quietly
      undoes it.
    */
    if (focusPoint) {
      map.jumpTo({ center: [focusPoint.lng, focusPoint.lat], zoom: focusPoint.zoom ?? 15 });

      return;
    }

    /*
      Where this city's map was left, if it has been looked at before.

      Switching between the single-day and the operation screen builds a
      new map, and framing the city again took somebody working a few
      streets back out to the whole of it. Only when the view is this
      city's own: a map opened on a different city has nothing of its to
      return to, and falls through to the framing below.
    */
    const left = lastSeen[city.id];
    if (left) {
      map.jumpTo({ center: [left.lng, left.lat], zoom: left.zoom });

      return;
    }

    const [minLng, minLat, maxLng, maxLat] = city.bbox;
    /*
     * Tighter than the bounding box, because a bounding box is a rectangle
     * around a shape that is not one.
     *
     * Washington DC is a diamond: fitting its box put the city small in the
     * middle of a wide screen with empty margins on both sides and no place
     * labels, which appear with zoom. Trimming the box and halving the
     * padding fills the view with the city somebody just chose.
     */
    /*
     * How much depends on how big the city is.
     *
     * A metro-sized box is nearly all city, so a quarter can come off each
     * side and what is left is the part anybody plans in — close enough for
     * street and neighbourhood labels, which is what makes a map readable
     * rather than decorative. A state-sized "city" is mostly the ground
     * between its towns, and cropping a quarter of Montana would hide the
     * half of it a traveller might drive to.
     */
    const span = Math.max(maxLng - minLng, maxLat - minLat);
    const trim = span < 1 ? 0.25 : 0.05;
    const lngInset = (maxLng - minLng) * trim;
    const latInset = (maxLat - minLat) * trim;
    map.fitBounds(
      [
        [minLng + lngInset, minLat + latInset],
        [maxLng - lngInset, maxLat - latInset],
      ],
      // Jumping rather than flying: a pan across continents is a long, useless
      // animation, and the destination is all anyone wants to see.
      { padding: 24, duration: 0, maxZoom: 15 },
    );
  }, [city.id, city.bbox, styleReady, focusPoint]);

  /*
   * Frame the two ends as soon as both are chosen, before anything is
   * planned.
   *
   * Choosing a start in one city and a destination two hundred miles away
   * used to leave the camera exactly where it was — over the city's bounding
   * box — so the journey being described was off screen while it was being
   * described. Only while there is no plan: once there is one, the itinerary
   * framing below is the better answer and this must not fight it.
   */
  const endpointKey = (pins ?? [])
    .filter((pin) => pin.role === 'start' || pin.role === 'end')
    .map((pin) => `${pin.role}:${pin.lat.toFixed(4)},${pin.lng.toFixed(4)}`)
    .join('|');
  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map || plan || !styleReady) return;
    const ends = (pins ?? []).filter((pin) => pin.role === 'start' || pin.role === 'end');
    if (ends.length === 0) return;

    const bounds = ends.reduce(
      (acc, pin) => [
        Math.min(acc[0], pin.lng), Math.min(acc[1], pin.lat),
        Math.max(acc[2], pin.lng), Math.max(acc[3], pin.lat),
      ],
      [Infinity, Infinity, -Infinity, -Infinity],
    );

    map.fitBounds(
      [[bounds[0], bounds[1]], [bounds[2], bounds[3]]],
      // A single end, or a round trip, has no extent — fitBounds on a point
      // zooms to the maximum, so hold a street-level zoom instead.
      { padding: 96, duration: 700, maxZoom: ends.length > 1 ? 15 : 14 },
    );
    // Keyed by the coordinates rather than the array, which is rebuilt on
    // every render and would refit the camera continuously.
  }, [endpointKey, plan, styleReady]);

  // Frame the whole itinerary whenever a new plan arrives.
  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map || !plan) return;

    const points: [number, number][] = plan.legs.flatMap((leg) => leg.geometry);
    for (const stop of plan.stops) points.push([stop.lng, stop.lat]);
    if (points.length === 0) return;

    const bounds = points.reduce(
      (acc, [lng, lat]) => [
        Math.min(acc[0], lng), Math.min(acc[1], lat),
        Math.max(acc[2], lng), Math.max(acc[3], lat),
      ],
      [Infinity, Infinity, -Infinity, -Infinity],
    );

    map.fitBounds(
      [[bounds[0], bounds[1]], [bounds[2], bounds[3]]],
      { padding: 72, duration: 900, maxZoom: 16 },
    );
  }, [plan]);

  /*
   * True while an endpoint drag is finishing.
   *
   * The browser fires a click on the marker when a drag ends on it, since
   * mousedown and mouseup both landed there. This marks that click as part
   * of the drag, so it does not also open the stop. Cleared on a timeout, so
   * a drag that ends without one cannot swallow a later real click.
   */
  const endpointDragged = useRef(false);

  if (!supported) {
    /*
     * No map, and instructions instead.
     *
     * The basemap is OpenMapTiles vector tiles, which MapLibre draws on the
     * GPU — without a WebGL context it draws nothing at all. There was once a
     * second, server-rasterised map here for this case; it was removed because
     * one map that everybody sees is worth more than two that drift apart.
     *
     * So this says what is wrong and how to fix it, rather than showing an
     * empty grey box. Almost every browser that lands here has WebGL and has
     * it switched off — a disabled hardware-acceleration setting, a blocklisted
     * driver, or a remote session — which is a thing the reader can act on.
     */
    return (
      <Box
        sx={{
          width: '100%', height: '100%', display: 'flex',
          alignItems: 'center', justifyContent: 'center', p: 3,
        }}
      >
        <Alert severity="warning" sx={{ maxWidth: 520 }}>
          <AlertTitle>The map needs WebGL</AlertTitle>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            This browser is not providing a WebGL context, so the map cannot be
            drawn. Everything else on this screen still works — only the map is
            affected.
          </Typography>
          <Typography variant="body2" component="div">
            To turn it on:
            <Box component="ul" sx={{ pl: 2.5, my: 0.75 }}>
              <li>
                Chrome or Edge — Settings → System → enable
                <strong> Use graphics acceleration when available</strong>, then
                restart the browser. Visit <code>chrome://gpu</code> to confirm
                WebGL now reads as enabled.
              </li>
              <li>
                Firefox — open <code>about:config</code> and set
                <strong> webgl.disabled</strong> to <code>false</code>.
              </li>
              <li>
                Safari — Settings → Advanced → Show Develop menu, then
                Develop → Experimental Features → enable WebGL.
              </li>
            </Box>
            On a remote desktop or a virtual machine, graphics acceleration is
            often unavailable to the browser regardless of these settings.
          </Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%', '& .maplibregl-ctrl-attrib': { fontSize: 10 } }}>
      <Map
        ref={mapRef}
        mapStyle={INITIAL_STYLE}
        initialViewState={{
          longitude: city.center.lng,
          latitude: city.center.lat,
          zoom: 12,
        }}
        onLoad={(event) => {
          /*
           * The hatch has to exist before any layer names it, and a style
           * change drops registered images — so it is added here, on every
           * load, rather than once on mount.
           */
          const map = event.target;
          onMapReady?.(map);
          const image = hatchImage(dark ? '#ffb27a' : '#8a4b12');
          if (image && !map.hasImage(HATCH_IMAGE)) {
            map.addImage(HATCH_IMAGE, image, { pixelRatio: 2 });
          }
          setStyleReady(true);
        }}
        onMoveEnd={(event) => {
          const centre = event.target.getCenter();
          const zoom = event.target.getZoom();
          lastSeen[city.id] = { lng: centre.lng, lat: centre.lat, zoom };

          const box = event.target.getBounds();
          onCamera?.({
            lat: centre.lat,
            lng: centre.lng,
            zoom,
            bounds: [box.getWest(), box.getSouth(), box.getEast(), box.getNorth()],
          });
        }}
        // A blank map with nothing logged is the worst outcome; surface it.
        onError={(event) => setMapError(event.error?.message ?? 'Map failed to load')}
        attributionControl={{
          compact: true,
          /*
           * Our name first, the data credits after it.
           *
           * The credits themselves stay. Planetiler's OpenMapTiles profile
           * requires "© OpenMapTiles © OpenStreetMap contributors" wherever
           * its vector tiles are shown, and the underlying OpenStreetMap data
           * is ODbL, which requires attribution as a condition of using it at
           * all — the same obligation the Foursquare credit in the place
           * dialog answers. Dropping them would not be a styling choice.
           *
           * This is how every commercial map does it: the brand reads first
           * and the sources follow, both inside the same collapsed panel.
           * Without a basemap only the OSM credit applies, since the places
           * and routes come straight from OSM.
           */
          customAttribution: basemapUrl
            ? [
              'Powered by TravPlaces',
            ]
            : ['Powered by TravPlaces'],
        }}
        style={{ width: '100%', height: '100%' }}
      >
        {/*
          Bottom right, so it stacks above the attribution rather than under
          the basemap toggle in the opposite corner. The compass is kept: the
          map can be rotated by accident with two fingers or ctrl-drag, and
          without it there is no way back to north.
        */}
        {/*
          Top left, because the right-hand side is spoken for.

          The assistant floats in the bottom-right corner and the layer
          controls sit top-right; a zoom control under a chat window is one
          nobody can press.
        */}
        <NavigationControl position="top-left" showCompass visualizePitch={false} />

        {pins?.map((pin, index) => {
          /*
           * A suggestion is not in the trip yet, and a break has nowhere
           * to be moved to, so neither answers to a drag. Everything else
           * does, whether it was named or dropped.
           */
          const movable = (Boolean(onPinDrag) || Boolean(pin.onMove))
            && pin.role !== 'suggested' && !pin.fixed;

          /* Ground already worked, which the ring below is drawn to say. */
          const worked = (pin.planned ?? 0) > 0;

          return (
            <Marker
              key={`pin-${index}`}
              longitude={pin.lng}
              latitude={pin.lat}
              anchor={
                pin.role === 'start' || pin.role === 'end' || pin.role === 'suggested'
                  ? 'center'
                  : 'bottom'
              }
              draggable={movable}
              onDragEnd={(event) => {
                const at = { lat: event.lngLat.lat, lng: event.lngLat.lng };
                if (pin.onMove) pin.onMove(at);
                else onPinDrag?.(index, at);
              }}
            >
              {/*
                Removed on a DOUBLE click, as the via points are.

                A marked spot sits exactly where somebody has been clicking
                to look around, so a single click would throw away a mark
                placed deliberately, and the only way back is to place it
                again by eye. The same gesture for both kinds of dropped
                point is one thing to learn rather than two.

                On a wrapper rather than on the Marker, because react-map-gl
                offers only onClick there — and the drawn shapes underneath
                differ per role, so one wrapper beats four sets of handlers.
              */}
              <Box
                sx={{ display: 'inline-flex' }}
                {...(pin.onRemove
                  ? {
                    onClick: (event: React.MouseEvent) => {
                      event.stopPropagation();
                      onPinHint?.(pin.role);
                    },
                    onDoubleClick: (event: React.MouseEvent) => {
                      event.stopPropagation();
                      pin.onRemove?.();
                    },
                  }
                  : {})}
              >
              <Tooltip
                arrow
                title={
                  /*
                   * Name over address over coordinate. A pin that resolved to a
                   * real place should read as that place; the coordinate is the
                   * last resort, not the headline.
                   */
                  <Box>
                    {STOP_ROLE_LABELS[pin.role] && (
                      <Typography variant="caption" sx={{ display: 'block', opacity: 0.75 }}>
                        {STOP_ROLE_LABELS[pin.role]}
                      </Typography>
                    )}
                    <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
                      {pin.label ?? `${pin.lat.toFixed(5)}, ${pin.lng.toFixed(5)}`}
                    </Typography>
                    {pin.address && (
                      <Typography variant="caption" sx={{ display: 'block', opacity: 0.85 }}>
                        {pin.address}
                      </Typography>
                    )}
                    {pin.label && (
                      <Typography variant="caption" sx={{ display: 'block', opacity: 0.6 }}>
                        {pin.lat.toFixed(5)}, {pin.lng.toFixed(5)}
                      </Typography>
                    )}
                    {pin.role === 'suggested' && pin.planned !== undefined && (
                      <Typography variant="caption" sx={{ display: 'block', opacity: 0.75 }}>
                        {worked
                          ? `In ${pin.planned} saved plan${pin.planned === 1 ? '' : 's'}`
                          : 'In no saved plan yet'}
                      </Typography>
                    )}
                    {/* A ring that opens is not obviously one, so it says so. */}
                    {pin.role === 'suggested' && pin.poiId && onPinClick && (
                      <Typography variant="caption" sx={{ display: 'block', opacity: 0.75, mt: 0.5 }}>
                        Click to read about it
                      </Typography>
                    )}
                    {pin.onRemove && (
                      <Typography variant="caption" sx={{ display: 'block', opacity: 0.75, mt: 0.5 }}>
                        Double-click to remove
                        {movable ? ', or drag to move' : ''}
                      </Typography>
                    )}
                  </Box>
                }
              >
                {/*
                  Start and end are drawn exactly as a planned route draws them,
                  so choosing them from the list and seeing them on the map agree
                  — the same S and E, before and after planning.
                */}
                {pin.role === 'start' || pin.role === 'end' ? (
                  <Box
                    sx={{
                      width: roundTrip && pin.role === 'start' ? 36 : 26,
                      height: 26,
                      borderRadius: roundTrip && pin.role === 'start' ? 13 : '50%',
                      fontSize: roundTrip && pin.role === 'start' ? 11 : 13,
                      bgcolor: pin.role === 'start' ? STOP_COLOURS.start : STOP_COLOURS.end,
                      color: '#fff',
                      display: 'grid', placeItems: 'center',
                      fontWeight: 700,
                      cursor: movable ? 'grab' : 'default',
                      '&:active': movable ? { cursor: 'grabbing' } : undefined,
                      border: `2px solid ${theme.palette.background.paper}`,
                      boxShadow: '0 1px 4px rgba(0,0,0,.35)',
                    }}
                  >
                    {pin.role === 'start' ? (roundTrip ? 'S/E' : 'S') : 'E'}
                  </Box>
                ) : pin.role === 'suggested' ? (
                  /*
                    Hollow, and smaller than a real stop.
                    A suggestion is a place somebody is still deciding about, and
                    drawing it the same as a chosen stop would make the map claim
                    a day far fuller than the one being planned. The ring fills in
                    when the cursor is on its row, which is what ties the list to
                    the map.
                  */
                  <Box
                    role={pin.poiId && onPinClick ? 'button' : undefined}
                    tabIndex={pin.poiId && onPinClick ? 0 : undefined}
                    onClick={(event) => {
                      if (!pin.poiId || !onPinClick) return;
                      // The map drops an avoidance pin wherever it is clicked,
                      // and a ring sits on top of the map.
                      event.stopPropagation();
                      onPinClick(pin.poiId);
                    }}
                    onKeyDown={(event) => {
                      if (!pin.poiId || !onPinClick) return;
                      if (event.key !== 'Enter' && event.key !== ' ') return;
                      event.preventDefault();
                      onPinClick(pin.poiId);
                    }}
                    sx={{
                      /*
                        Worked ground is drawn filled, and large enough to
                        carry its own figure; new ground stays the small
                        hollow ring it has always been. The two read apart
                        without a legend, which is the point of marking them
                        at all.
                      */
                      width: worked ? (pin.highlighted ? 22 : 18) : (pin.highlighted ? 16 : 11),
                      height: worked ? (pin.highlighted ? 22 : 18) : (pin.highlighted ? 16 : 11),
                      borderRadius: '50%',
                      bgcolor: worked
                        ? COVERED_COLOUR
                        : pin.highlighted ? STOP_COLOURS.visit : theme.palette.background.paper,
                      color: '#fff',
                      display: 'grid',
                      placeItems: 'center',
                      fontSize: 10,
                      fontWeight: 700,
                      lineHeight: 1,
                      border: `2.5px solid ${worked ? COVERED_COLOUR : STOP_COLOURS.visit}`,
                      /*
                        The chosen one, picked out of a field of its own
                        kind. A size difference alone was not enough among
                        a hundred rings the same colour, so it carries a
                        collar in the panel's own background and a dark
                        halo outside that — legible on a pale map and a
                        dark one, which a single shadow is not.
                      */
                      boxShadow: pin.highlighted
                        ? `0 0 0 3px ${theme.palette.background.paper}, 0 0 0 6px rgba(0,0,0,.35)`
                        : 'none',
                      transition: 'width .12s, height .12s',
                      cursor: pin.poiId && onPinClick ? 'pointer' : 'default',
                      /*
                         A ring is eleven pixels across, which is a hard thing to
                         hit and an easy one to miss by two. The padding is
                         transparent and sits outside the drawn circle, so the
                         target is comfortable while the map still looks the
                         same.
                      */
                      ...(pin.poiId && onPinClick
                        ? { outline: 'none', boxSizing: 'content-box', p: '7px', m: '-7px',
                            backgroundClip: 'content-box' }
                        : {}),
                    }}
                  >
                    {/*
                      The figure itself, on every marked ring.

                      Including a one: drawing the mark and withholding the
                      number left a filled ring saying nothing, which reads
                      as a fault rather than as "once". Nine plans and
                      ninety are both heavy and past that the count is a
                      smudge, so it stops at 9+ and the tooltip says the
                      rest.
                    */}
                    {worked ? ((pin.planned ?? 0) > 9 ? '9+' : pin.planned) : null}
                  </Box>
                ) : (
                  /*
                    A transparent pad around the triangle, which is drawn out of
                    borders and is fourteen pixels across. Aiming at that to pick
                    it up mostly lands on the map behind it, which pans instead.

                    The triangle sits at the bottom centre of the pad, where the
                    marker is anchored, so the tip still marks the spot.
                  */
                  <Box
                    sx={{
                      width: 30, height: 24,
                      display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
                      cursor: movable ? 'grab' : 'default',
                      '&:active': movable ? { cursor: 'grabbing' } : undefined,
                    }}
                  >
                    <Box
                      sx={{
                        width: 0, height: 0,
                        borderLeft: '7px solid transparent',
                        borderRight: '7px solid transparent',
                        borderTop: `14px solid ${
                          pin.role === 'avoid' ? AVOID_COLOUR : STOP_COLOURS.visit
                        }`,
                        filter: 'drop-shadow(0 1px 2px rgba(0,0,0,.4))',
                      }}
                    />
                  </Box>
                )}
              </Tooltip>
              </Box>
            </Marker>
          );
        })}

        {/*
          Where the route is being dragged to.

          Shown only during the drag, as the thing the cursor is carrying.
          There are no permanent handles: the line itself is what gets
          grabbed, so a dot sitting on it the rest of the time would be
          clutter standing for a gesture that needs no marker.
        */}
        {bend && onRouteBend && (
          <Marker longitude={bend.lng} latitude={bend.lat} anchor="center">
            <Box
              sx={{
                width: 13,
                height: 13,
                borderRadius: '50%',
                bgcolor: 'background.paper',
                border: 3,
                borderColor: MODE_COLOURS[plan?.legs[bend.leg]?.mode ?? 'car'],
                boxShadow: 3,
                pointerEvents: 'none',
              }}
            />
          </Marker>
        )}

        {/*
          Via points: small diamonds, deliberately unlike the numbered stop
          circles, since they are not places anyone visits.
        */}
        {viaPoints?.map((via, index) => (
          <Marker
            key={`via-${index}`}
            longitude={viaDrag?.index === index ? viaDrag.lng : via.lng}
            latitude={viaDrag?.index === index ? viaDrag.lat : via.lat}
            anchor="center"
            draggable={Boolean(onViaPointMove)}
            onDragStart={() => setViaDrag({ index, lng: via.lng, lat: via.lat })}
            onDrag={(event) => setViaDrag({
              index, lng: event.lngLat.lng, lat: event.lngLat.lat,
            })}
            onDragEnd={(event) => {
              setViaDrag(null);
              onViaPointMove?.(index, { lat: event.lngLat.lat, lng: event.lngLat.lng });
            }}
          >
            <Tooltip
              arrow
              title={onViaPointMove
                ? 'Routed through here — drag to move, double-click to remove'
                : 'Routed through here — double-click to remove'}
            >
              {/*
                A transparent pad several times the size of the diamond. The
                diamond sits on the route line, which carries a wide grab
                layer of its own; without a generous target here, aiming at
                the point and missing lands on the line instead.
              */}
              <Box
                /*
                 * Removed on a DOUBLE click, and a single one only says so.
                 *
                 * These sit on the drawn line, which is exactly where a
                 * traveller clicks to look at the route — so a single click
                 * was throwing away a point somebody had placed deliberately,
                 * and the only way back was to place it again by eye. A
                 * double click cannot be arrived at by accident, and asking
                 * for one is cheaper than a dialog over a 13px diamond.
                 */
                onClick={(event) => {
                  event.stopPropagation();
                  onViaPointHint?.(index);
                }}
                onDoubleClick={(event) => {
                  event.stopPropagation();
                  onViaPointClick?.(index);
                }}
                sx={{
                  width: 30,
                  height: 30,
                  display: 'grid',
                  placeItems: 'center',
                  cursor: onViaPointMove ? 'grab' : onViaPointClick ? 'pointer' : 'default',
                  '&:active': onViaPointMove ? { cursor: 'grabbing' } : undefined,
                  '&:hover .via-dot': {
                    bgcolor: AVOID_COLOUR,
                    borderColor: AVOID_COLOUR,
                    transform: 'rotate(45deg) scale(1.25)',
                  },
                }}
              >
                <Box
                  className="via-dot"
                  sx={{
                    width: 13,
                    height: 13,
                    bgcolor: 'background.paper',
                    border: `3px solid ${STOP_COLOURS.visit}`,
                    transform: 'rotate(45deg)',
                    boxShadow: '0 1px 3px rgba(0,0,0,.4)',
                    transition: 'transform .12s, background-color .12s, border-color .12s',
                  }}
                />
              </Box>
            </Tooltip>
          </Marker>
        ))}

        {plan?.stops.map((stop, index) => {
          const isEndpoint = stop.kind === 'start' || stop.kind === 'end';

          /*
           * On a round trip the start and the end are the same coordinate, so
           * one marker sits exactly on top of the other. Drawn plainly, the E
           * hides the S and the map reads as a route that ends somewhere it
           * never began. The pair is drawn once instead, marked as both.
           */
          if (isRoundTrip && stop.kind === 'end') return null;

          const label = isRoundTrip && stop.kind === 'start'
            ? 'S/E'
            : stop.kind === 'start'
              ? 'S'
              : stop.kind === 'end'
                ? 'E'
                : String(stopNumbers.get(index) ?? index);
          const highlighted = highlightedStopIndex === index;
          /* Gone past rather than stopped at: drawn as a via point below. */
          const passing = !isEndpoint && isPassedStop(stop);
          if (passing && passedDown) return null;
          // Only the ends are draggable: a numbered stop is a place the
          // route visits, changed by adding and removing it rather than by
          // moving it to a position that may hold nothing.
          const movable = Boolean(onEndpointDrag) && isEndpoint;

          return (
            <Marker
              key={`${stop.kind}-${index}`}
              longitude={stop.lng}
              latitude={stop.lat}
              anchor="center"
              draggable={movable}
              onDragStart={() => { endpointDragged.current = true; }}
              onDragEnd={(event) => {
                onEndpointDrag?.(
                  stop.kind === 'end' ? 'end' : 'start',
                  { lat: event.lngLat.lat, lng: event.lngLat.lng },
                );
                // Cleared after the click this drag is about to fire.
                window.setTimeout(() => { endpointDragged.current = false; }, 0);
              }}
              onClick={() => {
                if (endpointDragged.current) return;
                onStopClick?.(index);
              }}
            >
              <Tooltip
                arrow
                /*
                 * Dismissed the instant the pointer leaves.
                 *
                 * Stop markers sit a few pixels apart on a city map. With
                 * MUI's default leave delay and fade, moving between two of
                 * them shows the previous stop's time over the new marker —
                 * which reads as the map and the itinerary disagreeing.
                 */
                enterDelay={80}
                leaveDelay={0}
                disableInteractive
                slotProps={{ transition: { timeout: 0 } }}
                title={
                  `${
                    isRoundTrip && stop.kind === 'start'
                      ? 'S/E — Start and End (round trip)'
                      : STOP_ROLE_LABELS[stop.kind] ?? `Stop ${index}`
                  } — ${stop.name} · ${formatClock(stop.arrivalTime)}`
                  // Only a real place has anything more to show.
                  + (stop.poiId && onStopClick ? ' · click for details' : '')
                  // Names the gesture, which the marker cannot show by itself.
                  + (movable ? ' · drag to move it and re-plan' : '')
                }
              >
                {/*
                  A place the round goes past is drawn as a via point.

                  Hollow and turned on its corner, the same shape the points
                  that shape the line use — because that is what it is: the
                  route runs through it and stays nowhere. A filled disc says
                  "a stop is planned here", which for these would be a claim
                  about the day that is not true. The number stays, upright
                  inside it, so the list and the map still agree.
                */}
                {passing ? (
                  /*
                    Small, and numbered only when nothing else is.

                    A hundred of these over a city centre is a wall of badges
                    with the line they describe somewhere underneath, so
                    among numbered stops they stay blank at the 13px the
                    route-shaping points use — the shape says "the route runs
                    through here" and the discs beside them carry the count.
                    When the whole round is made of them there is nothing
                    else to count by, so each takes its number and the room
                    it needs.
                  */
                  <Box
                    sx={{
                      width: everyStopPassing
                        ? (highlighted ? 24 : 20)
                        : (highlighted ? 17 : 13),
                      height: everyStopPassing
                        ? (highlighted ? 24 : 20)
                        : (highlighted ? 17 : 13),
                      display: 'grid',
                      placeItems: 'center',
                      transform: 'rotate(45deg)',
                      bgcolor: theme.palette.background.paper,
                      border: `${everyStopPassing ? 2 : 3}px solid ${STOP_COLOURS.visit}`,
                      cursor: movable ? 'grab' : onStopClick ? 'pointer' : 'default',
                      '&:active': movable ? { cursor: 'grabbing' } : undefined,
                      boxShadow: highlighted
                        ? '0 0 0 4px rgba(29,111,106,.35)'
                        : '0 1px 3px rgba(0,0,0,.4)',
                      transition: 'width .15s, height .15s, box-shadow .15s',
                    }}
                  >
                    {everyStopPassing && (
                      <Box
                        component="span"
                        sx={{
                          transform: 'rotate(-45deg)',
                          fontSize: 9,
                          fontWeight: 700,
                          lineHeight: 1,
                          color: STOP_COLOURS.visit,
                        }}
                      >
                        {label}
                      </Box>
                    )}
                  </Box>
                ) : (
                <Box
                  sx={{
                    // "S/E" needs a pill; one letter or a number stays round.
                    width: label.length > 1 && isEndpoint
                      ? (highlighted ? 42 : 36)
                      : (highlighted ? 32 : 26),
                    height: highlighted ? 32 : 26,
                    borderRadius: label.length > 1 && isEndpoint ? 13 : '50%',
                    bgcolor: STOP_COLOURS[stop.kind],
                    color: '#fff',
                    display: 'grid',
                    placeItems: 'center',
                    fontSize: label.length > 1 && isEndpoint ? 11 : isEndpoint ? 13 : 12,
                    fontWeight: 700,
                    cursor: movable ? 'grab' : onStopClick ? 'pointer' : 'default',
                    '&:active': movable ? { cursor: 'grabbing' } : undefined,
                    border: `2px solid ${theme.palette.background.paper}`,
                    boxShadow: highlighted ? '0 0 0 4px rgba(29,111,106,.35)' : '0 1px 4px rgba(0,0,0,.35)',
                    transition: 'width .15s, height .15s, box-shadow .15s',
                  }}
                >
                  {label}
                </Box>
                )}
              </Tooltip>
            </Marker>
          );
        })}
      </Map>

      {/*
        * Right-click is not a discoverable gesture, so it is stated. Sits
        * bottom-left, clear of the attribution MapLibre puts bottom-right.
        */}
      {onMapClick && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 8,
            left: 8,
            zIndex: 4,
            px: 1,
            py: 0.4,
            borderRadius: 1,
            bgcolor: 'background.paper',
            opacity: 0.85,
            boxShadow: 1,
            pointerEvents: 'none',
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Right-click the map to mark a spot
          </Typography>
        </Box>
      )}

      {/*
        Two colours mean nothing without saying which is which. Shown only
        for a loop, where the outward and return legs overlap and the
        distinction actually matters.
      */}
      {isRoundTrip && turningLegIndex >= 0 && (
        <Stack
          direction="row"
          spacing={1.5}
          sx={{
            position: 'absolute', bottom: 8, right: 8, zIndex: 4,
            px: 1.25, py: 0.6, borderRadius: 1,
            bgcolor: 'background.paper', boxShadow: 1, opacity: 0.94,
          }}
        >
          {([
            ['Out', plan ? MODE_COLOURS[plan.mode] : MODE_COLOURS.car],
            ['Back', RETURN_LEG_COLOUR],
          ] as const).map(([label, colour]) => (
            <Stack key={label} direction="row" spacing={0.6} sx={{ alignItems: 'center' }}>
              <Box sx={{ width: 16, height: 3, borderRadius: 2, bgcolor: colour }} />
              <Typography variant="caption" color="text.secondary">{label}</Typography>
            </Stack>
          ))}
        </Stack>
      )}

      {/*
        The tasking in one line, over the map.

        Everything here is already in the itinerary panel — and the itinerary
        panel is behind the map on a narrow screen, and scrolled away on a
        wide one. What somebody looking AT the route wants is the four
        numbers that describe it, in the place they are already looking.

        Read-only and click-through: it never covers anything up for long,
        and it takes no gestures away from the map.
      */}
      {/*
        Put the passed places down, and pick them up again.

        Offered only once there are enough of them to be in the way, and
        sited over the map rather than in the form, because it answers a
        question somebody has while looking at the line.
      */}
      {passingCrowd && (
        <Chip
          size="small"
          icon={passedDown ? <VisibilityOutlinedIcon /> : <VisibilityOffOutlinedIcon />}
          label={passedDown
            ? `Show ${passingCount} passed places`
            : `Hide ${passingCount} passed places`}
          onClick={() => setViaHidden(!viaHidden)}
          sx={{
            /*
              Bottom left, just above the numbers it belongs with: both
              describe the round rather than the map, and the corner is the
              one somebody is already reading. Clear of the strip by a
              finger's width, so neither sits on the other.
            */
            position: 'absolute', bottom: 58, left: 8, zIndex: 4,
            bgcolor: 'background.paper', boxShadow: 3, fontWeight: 500,
          }}
        />
      )}

      {plan && (
        <Stack
          direction="row"
          divider={<Box sx={{ width: '1px', bgcolor: 'divider', alignSelf: 'stretch' }} />}
          sx={{
            position: 'absolute', bottom: 8, left: 8, zIndex: 4,
            borderRadius: 1, overflow: 'hidden',
            bgcolor: 'background.paper', boxShadow: 2, opacity: 0.94,
            pointerEvents: 'none',
            borderLeft: '3px solid',
            borderColor: MODE_COLOURS[plan.mode],
          }}
        >
          {([
            [`${(plan.totals.distanceMeters / 1000).toFixed(1)} km`, 'distance'],
            [formatHudDuration(plan.totals.totalMinutes), 'on task'],
            [
              String(plan.stops.filter((stop) => stop.kind !== 'start' && stop.kind !== 'end').length),
              'stops',
            ],
            ...((plan.avoidedZones ?? []).length > 0
              ? [[
                  String((plan.avoidedZones ?? []).filter((z) => !z.violated).length),
                  'held clear',
                ] as [string, string]]
              : []),
            ...(plan.infrastructureProximity
              ? [[
                  `${plan.infrastructureProximity.score}`,
                  'proximity',
                ] as [string, string]]
              : []),
          ] as [string, string][]).map(([value, label]) => (
            <Box key={label} sx={{ px: 1.25, py: 0.5, minWidth: 62 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 700, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}
              >
                {value}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: 10, letterSpacing: 0.5, textTransform: 'uppercase' }}
              >
                {label}
              </Typography>
            </Box>
          ))}
        </Stack>
      )}

      {/* Only while the path has been bent by hand. Clear of the zoom and
          compass, which the map keeps in this same corner. */}
      {viaPoints && viaPoints.length > 0 && onViaPointClick && (
        <Box sx={{ position: 'absolute', top: 10, left: 52, zIndex: 4 }}>
          <Chip
            size="small"
            label={`Path shaped by ${viaPoints.length} point${viaPoints.length === 1 ? '' : 's'} — straighten`}
            onDelete={() => { for (let i = viaPoints.length - 1; i >= 0; i--) onViaPointClick(i); }}
            deleteIcon={<CloseIcon />}
            sx={{ bgcolor: 'background.paper', boxShadow: 1 }}
          />
        </Box>
      )}

      {mapError && (
        <Alert
          severity="error"
          variant="filled"
          onClose={() => setMapError(null)}
          sx={{ position: 'absolute', top: 8, left: 8, right: 8, zIndex: 5 }}
        >
          {mapError}
        </Alert>
      )}
    </Box>
  );
}
